import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Label,
  Skeleton,
  Textarea,
} from "@nexus/ui";
import { vendorDetailsQueryOptions, useUpdateAnswers } from "@/lib/queries";
import { connectionNeedsResign } from "../lib/terms-compliance";
import { SelectAnswerPicker } from "./SelectAnswerPicker";
import type {
  TenantVendorConnection,
  VendorQuestionType,
} from "../types/marketplace.types";

interface AdjustGuidelinesDrawerProps {
  connection: TenantVendorConnection;
  tenantId: string;
  open: boolean;
  onClose: () => void;
  onResignRequired?: () => void;
}

function buildInitialAnswers(
  connection: TenantVendorConnection,
): Record<string, string> {
  return Object.fromEntries(connection.answers.map((a) => [a.questionId, a.value]));
}

function isAnswerFilled(type: VendorQuestionType, raw: string | undefined): boolean {
  if (!raw) return false;
  if (type === "SELECT") {
    try {
      return (JSON.parse(raw) as string[]).length > 0;
    } catch {
      return false;
    }
  }
  return !!raw.trim();
}

export function AdjustGuidelinesDrawer({
  connection,
  tenantId,
  open,
  onClose,
  onResignRequired,
}: AdjustGuidelinesDrawerProps) {
  const { t } = useTranslation();
  const vendorInactive = !connection.vendor.isActive;
  // Only fetch when the drawer is open — avoids N+1 fetches for every mounted card.
  const { data: vendor, isLoading, isError } = useQuery({
    ...vendorDetailsQueryOptions(connection.vendorId),
    enabled: open && !!connection.vendorId,
  });
  const updateMutation = useUpdateAnswers();
  const [answers, setAnswers] = useState<Record<string, string>>(
    () => buildInitialAnswers(connection),
  );

  // Re-initialize form from the connection's saved answers each time the drawer opens.
  // Including `connection` so stale closures don't persist if the prop updates.
  useEffect(() => {
    if (open) {
      setAnswers(buildInitialAnswers(connection));
      if (connectionNeedsResign(connection)) {
        onResignRequired?.();
      }
    }
  }, [open, connection]);

  const sortedQuestions = vendor
    ? [...vendor.questions].sort((a, b) => a.sortOrder - b.sortOrder)
    : [];

  const canSave =
    !vendorInactive &&
    vendor != null &&
    !vendor.questions.some(
      (q) => q.isRequired && !isAnswerFilled(q.type, answers[q.id]),
    );

  function setAnswer(questionId: string, value: string) {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  async function handleSave() {
    if (!vendor) return;
    // Send all questions (including cleared optional ones) so the server can
    // overwrite previously-saved values that the user has since cleared.
    const payload = sortedQuestions.map((q) => ({
      questionId: q.id,
      value: answers[q.id] ?? "",
    }));

    await updateMutation.mutateAsync({
      connectionId: connection.id,
      payload: { tenantId, answers: payload },
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !updateMutation.isPending && onClose()}>
      <DialogContent
        className="flex max-h-[88dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[600px]"
      >
        <DialogHeader className="flex-row items-start gap-3 space-y-0 border-b border-border px-6 py-5 pr-12">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Icon name="sliders-horizontal" size="sm" color="primary" />
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <DialogTitle>{t("marketplace.connections.adjust.title")}</DialogTitle>
            <DialogDescription>
              {t("marketplace.connections.adjust.subtitle", {
                vendorName: connection.vendor.name,
              })}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto bg-muted/30 px-6 py-6">
          {vendorInactive ? (
            <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
              {t("marketplace.connections.adjust.readOnlyInactive")}
            </p>
          ) : isLoading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
          ) : isError ? (
            <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
              {t("errors.tryAgain", "Failed to load questions. Please try again.")}
            </p>
          ) : sortedQuestions.length === 0 ? (
            <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
              {t("marketplace.wizard.questions.noQuestions")}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {sortedQuestions.map((question) => {
                const value = answers[question.id] ?? "";
                const qId = `adj-question-${question.id}`;

                if (question.type === "SELECT") {
                  return (
                    <SelectAnswerPicker
                      key={question.id}
                      variant="card"
                      question={question}
                      value={value}
                      onChange={(v) => setAnswer(question.id, v)}
                    />
                  );
                }

                return (
                  <div
                    key={question.id}
                    className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4"
                  >
                    <Label htmlFor={qId} className="text-sm font-medium">
                      {question.prompt}
                      {!question.isRequired && (
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          ({t("marketplace.wizard.questions.optional")})
                        </span>
                      )}
                    </Label>
                    {question.type === "TEXTAREA" ? (
                      <Textarea
                        id={qId}
                        value={value}
                        onChange={(e) => setAnswer(question.id, e.target.value)}
                        rows={3}
                        placeholder={
                          question.isRequired
                            ? undefined
                            : t(
                                "marketplace.wizard.questions.optionalPlaceholder",
                              )
                        }
                      />
                    ) : (
                      <Input
                        id={qId}
                        value={value}
                        onChange={(e) => setAnswer(question.id, e.target.value)}
                        placeholder={
                          question.isRequired
                            ? undefined
                            : t(
                                "marketplace.wizard.questions.optionalPlaceholder",
                              )
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2.5 border-t border-border px-6 py-4 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            {vendorInactive
              ? t("common.close", "Close")
              : t("marketplace.connections.adjust.cancel")}
          </Button>
          {!vendorInactive && (
            <Button
              type="button"
              disabled={!canSave || updateMutation.isPending || isLoading}
              onClick={handleSave}
            >
              {updateMutation.isPending
                ? t("marketplace.connections.adjust.saving")
                : t("marketplace.connections.adjust.save")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
